<template>
  <div class="lp-element-panel lp-table-panel">
    <!-- Whole table -->
    <template v-if="!isTableCellEditing">
      <field-group
          label="Position on page"
          compact>
        <position-controls
            :horizontal="shownAlignment.horizontal"
            :vertical="shownAlignment.vertical"
            @align="onPositionAlign"/>
      </field-group>
      <table-fill-section
          v-if="tableFillTable"
          :table="tableFillTable"
          :selected-objects-list="getSelectedObjectsList"
          :content-list="contentList"
          label="Background"
          scope="table"
          @change="onTableFillChanged"
      />
      <field-group label="Borders">
        <border-controls
            :options="tableBorderOptions"
            :value="tableBorderValue"
            @select="toggleTableBorder"/>
        <div class="lp-pair lp-border-pair">
          <div class="lp-pair__item">
            <div class="lp-sub-label">Color</div>
            <color-field
                :color="getTableBorderColor || '#000000'"
                :active="isTableBorderColorPickerOpen"
                @pick="onBorderColorPick($event, 'table')"/>
          </div>
          <div class="lp-pair__item">
            <div class="lp-sub-label">Style</div>
            <button
                :class="{ 'is-active': isTableLineStylePanelOpen }"
                type="button"
                class="lp-line-style-btn"
                @click.stop="toggleTableLineStylePanel">
              <span class="lp-line-style-btn__text">{{ lineStyleLabel(getTableLineDash, getTableBorderWidth) }}</span>
              <icon
                  name="chevron"
                  class="lp-line-style-btn__chevron"/>
            </button>
          </div>
        </div>
      </field-group>

      <side-popover
          ref="borderColorPopoverEl"
          :open="isTableBorderColorPickerOpen"
          :top="borderColorPopover.top"
          :left="borderColorPopover.left"
          title="Border color"
          @close="isTableBorderColorPickerOpen = false"
          @click.native.stop>
        <color-picker
            :color="getTableBorderColor || '#000'"
            :active-object="activeObjectForLeftPanel"
            :selected-objects-list="getSelectedObjectsList"
            :content-list="contentList"
            display-style="inline"
            variant="panel"
            color-type="outline"
            hide-alpha
            @color-changed="onTableBorderColorChanged"
        />
      </side-popover>
      <side-popover
          :open="isTableLineStylePanelOpen"
          :top="tableLineStylePanelPos.top"
          :left="tableLineStylePanelPos.left"
          title="Border style"
          @close="isTableLineStylePanelOpen = false"
          @click.native.stop>
        <div class="lp-line-styles">
          <button
              v-for="opt in tableLineStyleOptions"
              :key="opt.id"
              :class="{ 'is-active': getTableLineDash === opt.name }"
              type="button"
              class="lp-line-styles__option"
              @click="getTableLineDash !== opt.name && applyTableBorderPatch({ dash: opt.name })">
            <span class="lp-line-styles__radio"/>
            <icon
                :name="opt.icon"
                class="lp-line-styles__preview"/>
            <span>{{ opt.tooltip }}</span>
          </button>
        </div>
        <div class="lp-sub-label lp-weight__label">Border weight</div>
        <div class="lp-weight">
          <input
              :style="{ '--fill': weightFill(getTableBorderWidth) }"
              :value="getTableBorderWidth || borderWeightRange.min"
              :min="borderWeightRange.min"
              :max="borderWeightRange.max"
              step="1"
              type="range"
              class="lp-weight__range"
              @input="applyTableBorderPatch({ width: Number($event.target.value) })"
          >
          <label class="lp-weight__box">
            <input
                :value="getTableBorderWidth == null ? '--' : getTableBorderWidth"
                type="text"
                inputmode="numeric"
                class="border-weight-input"
                @input="onTableBorderWeightInput"
                @change="onTableBorderWeightCommit"
                @blur="onTableBorderWeightCommit"
                @keyup.enter="$event.target.blur()"
            >
            <span>px</span>
          </label>
        </div>
      </side-popover>
    </template>

    <!-- Selected cells -->
    <template v-if="isTableCellEditing && editingTableId">
      <info-banner
          icon="tableIcon"
          class="lp-table-panel__banner">
        {{ editingTableCells.length }} {{ editingTableCells.length === 1 ? 'cell' : 'cells' }} selected · Shift-click to add
      </info-banner>

      <field-group label="Typography">
        <font-family-select
            :value="tableCellStyles.fontFamily"
            :product="product"
            label="Font family"
            variant="panel"/>
        <div class="lp-pair">
          <div class="lp-pair__item">
            <div class="lp-sub-label">Size</div>
            <size-adjust-button
                :key="'table-font-size-' + tableCellStyles.fontSize + '-' + (editingTableActiveCell && editingTableActiveCell.r) + '-' + (editingTableActiveCell && editingTableActiveCell.c)"
                :value="tableCellStyles.fontSize"
                :scale="1"
                label="Size"
                class="lp-size-stepper"/>
          </div>
          <div class="lp-pair__item">
            <div class="lp-sub-label">Line spacing</div>
            <size-adjust-button
                :key="'table-space-' + tableCellStyles.lineHeight + '-' + (editingTableActiveCell && editingTableActiveCell.r) + '-' + (editingTableActiveCell && editingTableActiveCell.c)"
                :value="tableCellStyles.lineHeight"
                label="Line Spacing"
                class="lp-size-stepper"/>
          </div>
        </div>
        <div
            ref="cellColorRow"
            :class="{ 'is-active': $store.getters.getIsOpen }"
            class="lp-text-color">
          <color-selector
              :key="'table-color-' + (editingTableId || '') + '-' + (editingTableActiveCell && editingTableActiveCell.r) + '-' + (editingTableActiveCell && editingTableActiveCell.c) + '-' + tableCellStyles.color"
              :colorobject="tableCellStyles.color"
              label="Color"/>
        </div>
      </field-group>
      <side-popover
          ref="cellColorPopoverEl"
          :open="$store.getters.getIsOpen && isTableCellEditing"
          :top="cellColorPopover.top"
          :left="cellColorPopover.left"
          title="Text color"
          @close="$store.commit('setIsOpen', false)"
          @click.native.stop>
        <color-picker
            :color="tableCellStyles.color"
            :active-object="activeObjectForLeftPanel"
            :selected-objects-list="selectedObjectsList"
            :content-list="contentList"
            display-style="inline"
            variant="panel"
        />
      </side-popover>

      <field-group label="Style">
        <segmented-control
            :options="cellStyleOptions"
            :value="activeCellStyles"
            multiple
            @click="emitTableCellStyle({ [$event.value]: !tableCellStyles[$event.value] })"/>
      </field-group>

      <!-- Alignment (vertical + horizontal — maps to cell.vAlign / cell.align) -->
      <field-group label="Align text">
        <div class="lp-row">
          <segmented-control
              :options="cellAlignOptions"
              :value="tableCellStyles.align"
              class="lp-row__grow"
              @click="$event.value !== tableCellStyles.align && emitTableCellStyle({ align: $event.value })"/>
          <segmented-control
              :options="cellVAlignOptions"
              :value="tableCellStyles.vAlign"
              class="lp-row__fixed"
              @click="$event.value !== tableCellStyles.vAlign && emitTableCellStyle({ vAlign: $event.value })"/>
        </div>
      </field-group>

      <table-fill-section
          v-if="tableFillTable"
          :table="tableFillTable"
          :cells="editingTableCells"
          :selected-objects-list="getSelectedObjectsList"
          :content-list="contentList"
          label="Background"
          scope="cell"
          @change="onTableFillChanged"
      />

      <field-group label="Borders">
        <border-controls
            :options="cellBorderOptions"
            :value="cellBorderValue"
            :disabled="cellBorderDisabled"
            @select="toggleCellBorderSide"/>
        <div class="lp-pair lp-border-pair">
          <div class="lp-pair__item">
            <div class="lp-sub-label">Color</div>
            <color-field
                :color="getCellBorderColor || '#000000'"
                :active="isCellBorderColorPickerOpen"
                @pick="onBorderColorPick($event, 'cell')"/>
          </div>
          <div class="lp-pair__item">
            <div class="lp-sub-label">Style</div>
            <button
                :class="{ 'is-active': isCellLineStylePanelOpen }"
                type="button"
                class="lp-line-style-btn"
                @click.stop="toggleCellLineStylePanel">
              <span class="lp-line-style-btn__text">{{ lineStyleLabel(getCellLineDash, getCellBorderWidth) }}</span>
              <icon
                  name="chevron"
                  class="lp-line-style-btn__chevron"/>
            </button>
          </div>
        </div>
      </field-group>

      <side-popover
          ref="borderColorPopoverEl"
          :open="isCellBorderColorPickerOpen"
          :top="borderColorPopover.top"
          :left="borderColorPopover.left"
          title="Border color"
          @close="isCellBorderColorPickerOpen = false"
          @click.native.stop>
        <color-picker
            :color="getCellBorderColor || '#000'"
            :active-object="activeObjectForLeftPanel"
            :selected-objects-list="getSelectedObjectsList"
            :content-list="contentList"
            display-style="inline"
            variant="panel"
            color-type="outline"
            hide-alpha
            @color-changed="onCellBorderColorChanged"
        />
      </side-popover>
      <side-popover
          :open="isCellLineStylePanelOpen"
          :top="cellLineStylePanelPos.top"
          :left="cellLineStylePanelPos.left"
          title="Border style"
          :width="333"
          @close="isCellLineStylePanelOpen = false"
          @click.native.stop>
        <div class="lp-line-styles">
          <button
              v-for="opt in tableLineStyleOptions"
              :key="'cell-' + opt.id"
              :class="{ 'is-active': getCellLineDash === opt.name }"
              type="button"
              class="lp-line-styles__option"
              @click="getCellLineDash !== opt.name && applyCellBorderPatch({ dash: opt.name })">
            <span class="lp-line-styles__radio"/>
            <icon
                :name="opt.icon"
                class="lp-line-styles__preview"/>
            <span>{{ opt.tooltip }}</span>
          </button>
        </div>
        <div class="lp-sub-label lp-weight__label">Border weight</div>
        <div class="lp-weight">
          <input
              :style="{ '--fill': weightFill(getCellBorderWidth) }"
              :value="getCellBorderWidth || borderWeightRange.min"
              :min="borderWeightRange.min"
              :max="borderWeightRange.max"
              step="1"
              type="range"
              class="lp-weight__range"
              @input="applyCellBorderPatch({ width: Number($event.target.value) })"
          >
          <label class="lp-weight__box">
            <input
                :value="getCellBorderWidth == null ? '--' : getCellBorderWidth"
                type="text"
                inputmode="numeric"
                class="border-weight-input"
                @input="onCellBorderWeightInput"
                @change="onCellBorderWeightCommit"
                @blur="onCellBorderWeightCommit"
                @keyup.enter="$event.target.blur()"
            >
            <span>px</span>
          </label>
        </div>
      </side-popover>
    </template>
  </div>
</template>

<script>
import EventBus from '@/EventBus';
import { findObjectById } from '@/components/Canvas/utils/groupHelpers';
import {
  ensureTableBorders,
  ensureSegmentBorders,
  applyToTargetBorders,
  applyToSelectedCellBorders,
  sharedTargetValue,
  getTableBorderTargets,
  cellSelectionEdgeRefs,
} from '@/components/DesignObjects/TableObject';
import FontFamilySelector from '@/components/FontFamilySelector/FontFamilySelector';
import SizeAdjustButton from '@/components/SizeAdjustButton/SizeAdjustButton';
import ColorSelector from '@/components/ColorSelector/ColorSelector';
import ColorPicker from '@/components/ColorPicker/ColorPicker';
import TableFillSection from '@/components/LeftSection/TableFillSection';
import panelContext from '@/components/LeftSection/mixins/panelContext';
import objectAlignment from '@/components/LeftSection/mixins/objectAlignment';
import sidePopover from '@/components/LeftSection/mixins/sidePopover';
import FieldGroup from '@/components/LeftSection/ui/FieldGroup';
import SegmentedControl from '@/components/LeftSection/ui/SegmentedControl';
import PositionControls from '@/components/LeftSection/ui/PositionControls';
import BorderControls from '@/components/LeftSection/ui/BorderControls';
import ColorField from '@/components/LeftSection/ui/ColorField';
import InfoBanner from '@/components/LeftSection/ui/InfoBanner';
import SidePopover from '@/components/LeftSection/ui/SidePopover';

const LINE_STYLE_LABELS   = {
  solid:        'Solid',
  dash:         'Dashed',
  'dash-short': 'Dashed short',
  dotted:       'Dotted',
};
const CELL_STYLE_OPTIONS  = [
  { value: 'bold', icon: 'boldicon', tooltip: 'Bold' },
  { value: 'italic', icon: 'Italic', tooltip: 'Italic' },
  { value: 'underline', icon: 'underlineicon', tooltip: 'Underline' },
];
const CELL_ALIGN_OPTIONS  = [
  { value: 'left', icon: 'text-horizontal-align-left', tooltip: 'Left' },
  { value: 'center', icon: 'text-horizontal-align-center', tooltip: 'Center' },
  { value: 'right', icon: 'text-horizontal-align-right', tooltip: 'Right' },
  { value: 'justify', icon: 'justify-text', tooltip: 'Justify' },
];
const CELL_VALIGN_OPTIONS = [
  { value: 'top', icon: 'textaligntop', tooltip: 'Top' },
  { value: 'middle', icon: 'textaligncenter', tooltip: 'Middle' },
  { value: 'bottom', icon: 'text-align-bottom', tooltip: 'Bottom' },
];

export default {
  name:       'TablePanel',
  components: {
    'font-family-select': FontFamilySelector,
    'size-adjust-button': SizeAdjustButton,
    'color-selector':     ColorSelector,
    'color-picker':       ColorPicker,
    'table-fill-section': TableFillSection,
    'field-group':        FieldGroup,
    'segmented-control':  SegmentedControl,
    'position-controls':  PositionControls,
    'border-controls':    BorderControls,
    'color-field':        ColorField,
    'info-banner':        InfoBanner,
    'side-popover':       SidePopover,
  },
  mixins: [panelContext, objectAlignment, sidePopover],
  props:  {
    width:                  { type: Number, default: 0 },
    height:                 { type: Number, default: 0 },
    productCrop:            { type: Object, default: null },
    // cell selection, owned by LeftSection (see header comment)
    isTableCellEditing:     { type: Boolean, default: false },
    editingTableId:         { type: [String, Number], default: null },
    editingTableSel:        { type: Object, default: null },
    editingTableActiveCell: { type: Object, default: null },
    tableCellStyles:        { type: Object, required: true },
  },
  data() {
    return {
      isTableBorderColorPickerOpen: false,
      isTableLineStylePanelOpen:    false,
      tableLineStylePanelPos:       { top: 0, left: 0 },
      borderWeightRange:            { min: 1, max: 20 },
      tableBorderOptions:           [
        {
          id:      'table-all-borders', name:    'all', tooltip: 'All Borders', icon:    'tableAllBorders',
        },
        {
          id:      'table-outer-borders', name:    'outer', tooltip: 'Outer Borders', icon:    'tableOuterBorders',
        },
        {
          id:      'table-inner-borders', name:    'inner', tooltip: 'Inner Borders', icon:    'tableInnerBorders',
        },
        {
          id:      'table-top-border', name:    'top', tooltip: 'Top Border', icon:    'tableTopBorder',
        },
        {
          id:      'table-bottom-border', name:    'bottom', tooltip: 'Bottom Border', icon:    'tableBottomBorder',
        },
        {
          id:      'table-left-border', name:    'left', tooltip: 'Left Border', icon:    'tableLeftBorder',
        },
        {
          id:      'table-right-border', name:    'right', tooltip: 'Right Border', icon:    'tableRightBorder',
        },
        {
          id:      'table-horizontal-inner', name:    'horizontal-inner', tooltip: 'Horizontal Inner Borders', icon:    'tableHorizontalInner',
        },
        {
          id:      'table-vertical-inner', name:    'vertical-inner', tooltip: 'Vertical Inner Borders', icon:    'tableVerticalInner',
        },
      ],
      tableLineStyleOptions: [
        {
          id:      'table-line-solid', name:    'solid', tooltip: 'Solid', icon:    'tableLineSolid',
        },
        {
          id:      'table-line-dash', name:    'dash', tooltip: 'Dashed', icon:    'tableLineDash',
        },
        {
          id:      'table-line-dash-short', name:    'dash-short', tooltip: 'Dashed short', icon:    'tableLineDashShort',
        },
        {
          id:      'table-line-dotted', name:    'dotted', tooltip: 'Dotted', icon:    'tableLineDotted',
        },
      ],
      cellBorderOptions: [
        {
          id:      'cell-all-borders', name:    'all', tooltip: 'All Borders', icon:    'tableAllBorders',
        },
        {
          id:      'cell-outer-borders', name:    'outer', tooltip: 'Outer Borders', icon:    'tableOuterBorders',
        },
        {
          id:      'cell-inner-borders', name:    'inner', tooltip: 'Inner Borders', icon:    'tableInnerBorders',
        },
        {
          id:      'cell-top-border', name:    'top', tooltip: 'Top Border', icon:    'tableTopBorder',
        },
        {
          id:      'cell-bottom-border', name:    'bottom', tooltip: 'Bottom Border', icon:    'tableBottomBorder',
        },
        {
          id:      'cell-left-border', name:    'left', tooltip: 'Left Border', icon:    'tableLeftBorder',
        },
        {
          id:      'cell-right-border', name:    'right', tooltip: 'Right Border', icon:    'tableRightBorder',
        },
        {
          id:      'cell-horizontal-inner', name:    'horizontal-inner', tooltip: 'Horizontal Inner Borders', icon:    'tableHorizontalInner',
        },
        {
          id:      'cell-vertical-inner', name:    'vertical-inner', tooltip: 'Vertical Inner Borders', icon:    'tableVerticalInner',
        },
      ],
      cellBorderSides:             ['all'],
      isCellBorderColorPickerOpen: false,
      isCellLineStylePanelOpen:    false,
      cellLineStylePanelPos:       { top: 0, left: 0 },
      cellStyleOptions:            CELL_STYLE_OPTIONS,
      cellAlignOptions:            CELL_ALIGN_OPTIONS,
      cellVAlignOptions:           CELL_VALIGN_OPTIONS,
      borderColorPopover:          { top: 0, left: 0 },
      cellColorPopover:            { top: 0, left: 0 },
    };
  },
  computed: {
    // Every cell the table has selected; shift-click adds cells to sel.cells.
    editingTableCells() {
      const sel  = this.editingTableSel;
      const cell = this.editingTableActiveCell;
      if (sel && sel.cells && sel.cells.length) { return sel.cells; }
      return cell ? [{ r: cell.r, c: cell.c }] : [];
    },
    // The picked buttons, minus any the current selection has no edges for.
    activeCellBorderSides() {
      return (this.cellBorderSides || []).filter(name => !this.isCellBorderDisabled(name));
    },
    // The border lines those buttons point at, across the whole cell selection.
    cellBorderRefs() {
      return cellSelectionEdgeRefs(
          this.activeObjectForLeftPanel,
          this.editingTableCells,
          this.activeCellBorderSides,
      );
    },
    cellTargetSpecs() {
      return this.cellBorderRefs.map(ref => ref.arr[ref.i]);
    },
    getCellLineDash() {
      return this.sharedCellBorderValue('dash') || 'solid';
    },
    getCellBorderWidth() {
      return this.sharedCellBorderValue('width');
    },
    getCellBorderColor() {
      return this.sharedCellBorderValue('color') || '#000000';
    },
    getTableLineDash() {
      const obj = this.activeObjectForLeftPanel;
      if (!obj || obj.type !== 'table') { return 'solid'; }
      return sharedTargetValue(obj, 'dash', getTableBorderTargets(obj)) || 'solid';
    },
    getTableBorderWidth() {
      const obj = this.activeObjectForLeftPanel;
      if (!obj || obj.type !== 'table') { return 1; }
      return sharedTargetValue(obj, 'width', getTableBorderTargets(obj)); // null → "--"
    },
    getTableBorderColor() {
      const obj = this.activeObjectForLeftPanel;
      if (!obj || obj.type !== 'table') { return '#000000'; }
      return sharedTargetValue(obj, 'color', getTableBorderTargets(obj)) || '#000000';
    },
    tableFillTable() {
      const editing = this.editingTableId
          ? findObjectById(this.contentList, this.editingTableId)
          : null;
      if (editing && editing.type === 'table') { return editing; }
      const active = this.activeObjectForLeftPanel;
      return (active && active.type === 'table') ? active : null;
    },
    tableBorderValue() {
      return this.tableBorderOptions.filter(border => this.isTableBorderOn(border.name)).map(border => border.name);
    },
    cellBorderValue() {
      return this.cellBorderOptions.filter(border => this.isCellBorderSideOn(border.name)).map(border => border.name);
    },
    cellBorderDisabled() {
      return this.cellBorderOptions.filter(border => this.isCellBorderDisabled(border.name)).map(border => border.name);
    },
    activeCellStyles() {
      return ['bold', 'italic', 'underline'].filter(key => this.tableCellStyles[key]);
    },
  },
  watch: {
    '$store.getters.getIsOpen': {
      handler(open) {
        if (!open || !this.isTableCellEditing) { return; }
        // only one colour popover at a time
        this.isTableBorderColorPickerOpen = false;
        this.isCellBorderColorPickerOpen  = false;
        this.$nextTick(() => {
          this.cellColorPopover = this.sidePopoverPosition(this.$refs.cellColorRow, { width: 333, height: 520 });
          this.cellColorPopover = this.fitSidePopover(this.$refs.cellColorPopoverEl, this.cellColorPopover);
        });
      },
      immediate: true,
    },
  },
  methods: {
    sharedCellBorderValue(key) {
      const specs = this.cellTargetSpecs;
      if (!specs.length) { return null; }
      return specs.every(s => s[key] === specs[0][key]) ? specs[0][key] : null;
    },
    openCellBorderColorPicker() {
      this.isCellLineStylePanelOpen    = false;
      this.isCellBorderColorPickerOpen = !this.isCellBorderColorPickerOpen;
    },
    onCellBorderColorChanged(payload) {
      const hex = (payload && payload.hex) || payload;
      if (!hex || typeof hex !== 'string') { return; }
      this.applyCellBorderPatch({ color: hex });
    },
    openTableBorderColorPicker() {
      this.isTableLineStylePanelOpen    = false;
      this.isCellLineStylePanelOpen     = false;
      this.isTableBorderColorPickerOpen = !this.isTableBorderColorPickerOpen;
    },
    clampBorderWeight(raw) {
      const { min, max } = this.borderWeightRange;
      const parsed       = parseInt(raw, 10);
      if (Number.isNaN(parsed)) {
        return null;
      }
      return Math.min(max, Math.max(min, parsed));
    },
    borderWeightDisplay(value) {
      return value == null ? '--' : String(value);
    },
    onTableBorderWeightInput(event) {
      const width = this.clampBorderWeight(event.target.value);
      if (width == null) {
        return;
      }
      this.applyTableBorderPatch({ width });
    },
    onTableBorderWeightCommit(event) {
      const width = this.clampBorderWeight(event.target.value);
      if (width == null) {
        event.target.value = this.borderWeightDisplay(this.getTableBorderWidth);
        return;
      }
      event.target.value = String(width);
      this.applyTableBorderPatch({ width });
    },
    onCellBorderWeightInput(event) {
      const width = this.clampBorderWeight(event.target.value);
      if (width == null) {
        return;
      }
      this.applyCellBorderPatch({ width });
    },
    onCellBorderWeightCommit(event) {
      const width = this.clampBorderWeight(event.target.value);
      if (width == null) {
        event.target.value = this.borderWeightDisplay(this.getCellBorderWidth);
        return;
      }
      event.target.value = String(width);
      this.applyCellBorderPatch({ width });
    },
    toggleTableLineStylePanel(event) {
      if (this.isTableLineStylePanelOpen) {
        this.isTableLineStylePanelOpen = false;
        return;
      }
      this.isTableBorderColorPickerOpen = false;
      this.isCellLineStylePanelOpen     = false;
      this.positionTableLineStylePanel(event && event.currentTarget);
      this.isTableLineStylePanelOpen = true;
    },
    // Stage 2: the Border style popover now opens beside the left panel like the other side popovers.
    lineStylePanelPosFor(trigger) {
      if (!trigger || !trigger.getBoundingClientRect) { return null; }
      return this.sidePopoverPosition(trigger, { width: 300, height: 290 });
    },
    positionTableLineStylePanel(trigger) {
      const pos = this.lineStylePanelPosFor(trigger);
      if (!pos) {
        return;
      }
      this.tableLineStylePanelPos = pos;
    },
    positionCellLineStylePanel(trigger) {
      const pos = this.lineStylePanelPosFor(trigger);
      if (!pos) {
        return;
      }
      this.cellLineStylePanelPos = pos;
    },
    toggleCellLineStylePanel(event) {
      if (this.isCellLineStylePanelOpen) {
        this.isCellLineStylePanelOpen = false;
        return;
      }
      this.isCellBorderColorPickerOpen = false;
      this.isTableLineStylePanelOpen   = false;
      this.positionCellLineStylePanel(event && event.currentTarget);
      this.isCellLineStylePanelOpen = true;
    },
    applyTableBorderPatch(patch) {
      const obj = this.activeObjectForLeftPanel;
      if (!obj || obj.type !== 'table') { return; }

      ensureSegmentBorders(obj);

      const targets = getTableBorderTargets(obj);
      if (!targets.length) { return; }

      applyToTargetBorders(
          obj,
          patch,
          targets,
          (arr, i, next) => { this.$set(arr, i, next); },
      );

      this.$set(obj, 'borderEpoch', (obj.borderEpoch || 0) + 1);
      EventBus.$emit('addIntoPageWiseUndoRedoArray', 'table: border');
    },
    onTableBorderColorChanged(payload) {
      const hex = (payload && payload.hex) || payload;
      if (!hex || typeof hex !== 'string') { return; }
      this.applyTableBorderPatch({ color: hex });
    },
    isTableBorderOn(name) {
      const obj = this.activeObjectForLeftPanel;
      if (!obj || obj.type !== 'table') { return name === 'all'; }
      const targets = getTableBorderTargets(obj);
      if (name === 'all') { return targets.length === 1 && targets[0] === 'all'; }
      return targets.includes(name);
    },
    toggleTableBorder(name) {
      const obj = this.activeObjectForLeftPanel;
      if (!obj || obj.type !== 'table') { return; }
      ensureTableBorders(obj);

      let next = getTableBorderTargets(obj);
      if (name === 'all') {
        next = ['all'];
      } else {
        next    = next.filter(s => s !== 'all');
        const i = next.indexOf(name);
        if (i >= 0) { next.splice(i, 1); } else { next.push(name); }
      }

      this.$set(obj, 'borderTargets', next);
      this.$set(obj, 'borderStyle', next.length === 1 ? next[0] : (next[0] || 'all'));
    },
    // A button is dead when the selection holds no such edge: no inner vertical
    // divide unless two selected cells sit side by side, no inner horizontal one
    // unless two sit one above the other, and neither of them for a single cell.
    isCellBorderDisabled(name) {
      return !cellSelectionEdgeRefs(
          this.activeObjectForLeftPanel,
          this.editingTableCells,
          name,
      ).length;
    },
    isCellBorderSideOn(name) {
      if (this.isCellBorderDisabled(name)) { return false; }
      const targets = this.activeCellBorderSides;
      if (name === 'all') { return targets.length === 1 && targets[0] === 'all'; }
      return targets.includes(name);
    },
    toggleCellBorderSide(name) {
      if (this.isCellBorderDisabled(name)) { return; }

      let next = this.activeCellBorderSides;
      if (name === 'all') {
        next = ['all'];
      } else {
        next    = next.filter(s => s !== 'all');
        const i = next.indexOf(name);
        if (i >= 0) { next.splice(i, 1); } else { next.push(name); }
      }

      this.cellBorderSides = next;
    },
    applyCellBorderPatch(patch) {
      const table = this.activeObjectForLeftPanel;
      if (!table || table.type !== 'table') { return; }

      ensureSegmentBorders(table);

      const sides = this.activeCellBorderSides;
      if (!sides.length || !this.editingTableCells.length) { return; }

      applyToSelectedCellBorders(
          table,
          this.editingTableCells,
          sides,
          patch,
          (arr, i, next) => { this.$set(arr, i, next); },
      );

      this.$set(table, 'borderEpoch', (table.borderEpoch || 0) + 1);
      EventBus.$emit('addIntoPageWiseUndoRedoArray', 'table: cell border');
    },
    onTableFillChanged() {
      EventBus.$emit('addIntoPageWiseUndoRedoArray', 'table: cell background');
    },
    // The cell style itself is applied by LeftSection (it owns tableCellStyles).
    emitTableCellStyle(patch) {
      this.$emit('cell-style', patch);
    },
    // LeftSection forwards the EventBus 'closeLeftPanelPopovers' event and its own outside clicks here.
    closeLeftPanelPopovers() {
      this.isTableLineStylePanelOpen = false;
      this.isCellLineStylePanelOpen  = false;
    },
    // LeftSection forwards 'tableCellSelectionCleared' here (it resets the cell state itself).
    onTableCellSelectionCleared() {
      this.isCellLineStylePanelOpen    = false;
      this.isCellBorderColorPickerOpen = false;
    },
    // ---- new UI (Stage 2) ----
    onBorderColorPick(e, scope) {
      e.stopPropagation();
      const isOpen = scope === 'table' ? this.isTableBorderColorPickerOpen : this.isCellBorderColorPickerOpen;
      if (!isOpen) {
        // only one colour popover at a time
        this.$store.commit('setIsOpen', false);
        this.isTableBorderColorPickerOpen = false;
        this.isCellBorderColorPickerOpen  = false;
        this.borderColorPopover           = this.sidePopoverPosition(e.currentTarget, { width: 333, height: 520 });
      }
      if (scope === 'table') {
        this.openTableBorderColorPicker();
      } else {
        this.openCellBorderColorPicker();
      }
      if (!isOpen) {
        this.$nextTick(() => {
          this.borderColorPopover = this.fitSidePopover(this.$refs.borderColorPopoverEl, this.borderColorPopover);
        });
      }
    },
    lineStyleLabel(dash, width) {
      return `${LINE_STYLE_LABELS[dash] || 'Solid'} · ${width == null ? '--' : width}px`;
    },
    weightFill(value) {
      const { min, max } = this.borderWeightRange;
      const current      = Math.min(max, Math.max(min, Number(value) || min));
      return `${((current - min) / (max - min)) * 100}%`;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/lp-tokens';
@import '../styles/lp-panel';

.lp-table-panel__banner {
  margin-top: 4px;
}

// "— Solid · 1 px  >" button that opens the Border style popover
.lp-line-style-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  height: $lp-control-h;
  padding: 0 6px 0 10px;
  border: 1px solid transparent;
  border-radius: $lp-radius-control;
  background: $lp-fill;
  font-family: $lp-sans;
  font-size: $lp-fs-control;
  color: $lp-ink;
  cursor: pointer;

  &:hover {
    border-color: $lp-line;
  }

  &.is-active {
    border-color: $lp-accent;
  }
}

.lp-line-style-btn__text {
  flex: 1 1 auto;
  text-align: left;
  white-space: nowrap;
}

// Borders "Color | Style": the Style button takes the width its label needs ("Dashed short · 15px"),
// the colour field gets the rest, so the style name is never cut off.
.lp-border-pair {
  grid-template-columns: minmax(0, 1fr) auto;
}

.lp-line-style-btn__chevron {
  flex: 0 0 auto;
  min-width: 0;
  min-height: 0;
  transform: rotate(-90deg);

  ::v-deep svg {
    width: 12px;
    height: 12px;
  }
}

.lp-line-styles {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.lp-line-styles__option {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 38px;
  padding: 0 10px;
  border: 0;
  border-radius: $lp-radius-control;
  background: transparent;
  font-family: $lp-sans;
  font-size: $lp-fs-control;
  color: $lp-ink;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: $lp-fill;
  }

  &.is-active {
    background: $lp-accent-soft;

    .lp-line-styles__radio {
      border-color: $lp-accent;
      box-shadow: inset 0 0 0 3px $white;
      background: $lp-accent;
    }
  }
}

.lp-line-styles__radio {
  flex: 0 0 16px;
  height: 16px;
  border: 1.5px solid #9AA0AA;
  border-radius: 50%;
  background: $white;
}

.lp-line-styles__preview {
  flex: 0 0 auto;
  min-width: 0;
  min-height: 0;
}

.lp-weight__label {
  margin-top: 14px;
}

.lp-weight {
  display: flex;
  align-items: center;
  gap: 12px;
}

.lp-weight__range {
  flex: 1 1 auto;
  min-width: 0;
  height: 4px;
  border-radius: 4px;
  outline: none;
  background: linear-gradient(90deg, $lp-accent var(--fill), $lp-line var(--fill));
  -webkit-appearance: none;
  appearance: none;

  &::-webkit-slider-thumb {
    width: 16px;
    height: 16px;
    border: 2px solid $white;
    border-radius: 50%;
    background: $lp-accent;
    box-shadow: 0 1px 3px rgba(20, 22, 27, 0.3);
    cursor: pointer;
    -webkit-appearance: none;
  }

  &::-moz-range-thumb {
    width: 14px;
    height: 14px;
    border: 2px solid $white;
    border-radius: 50%;
    background: $lp-accent;
    cursor: pointer;
  }
}

.lp-weight__box {
  display: flex;
  flex: 0 0 72px;
  align-items: center;
  height: 34px;
  margin: 0;
  padding: 0 10px;
  border-radius: $lp-radius-control;
  background: $lp-fill;
  font-family: $lp-mono;
  font-size: $lp-fs-control;
  color: $lp-muted;

  input {
    width: 100%;
    min-width: 0;
    border: 0;
    outline: none;
    background: transparent;
    font: inherit;
    color: $lp-ink;
  }
}
</style>
